import {jsPDF} from "jspdf";
import "jspdf-autotable";

const frequencies = {
    W: "Weekly",
    M: "Monthly",
    F: "Fortnightly",
    A: "Annually",
    "4W": "4-Weekly",
    Q: "Quarterly",
};

const statementHeaders = {
    income: "Income",
    fixed: "Fixed Expenditure",
    savings: "Savings",
    flexible: "Flexible Expenditure",
    debts: "Additional Debt",
};

const loadImage = (url) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = () => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(xhr.response);
        };
        xhr.onerror = reject;
        xhr.open("GET", url);
        xhr.responseType = "blob";
        xhr.send();
    });
};

export const processData = (data) => {
    return Object.keys(data).map((key) => {
        const name = data[key]["title"] ? data[key]["title"] : data[key]["key"];
        const freq = frequencies[data[key]["frequency"]] || "";
        const amount = data[key]["value"] || 0;
        return [name, freq, `£${amount.toFixed(2)}`];
    });
};

/** Renders one section with fixed 3-column layout and a foot-row total */
function renderSection(doc, printableW, margin, sectionTitle, rows, total) {
    if (!rows || rows.length === 0) return;

    const prevY = doc.autoTable.previous?.finalY || 60;
    const y = prevY + 15;

    doc.setFontSize(14).setFont("helvetica", "bold");
    doc.text(`My ${sectionTitle}`, margin.left, y);

    // 60% / 20% / 20% fixed widths
    const colW = [printableW * 0.6, printableW * 0.2, printableW * 0.2];

    doc.autoTable({
        startY: y + 5,
        head: [[sectionTitle, "Frequency", "Amount"]],
        body: rows,
        foot: [["Total:", "", `£${(total || 0).toFixed(2)}`]],
        showHead: "everyPage",
        showFoot: "lastPage",
        theme: "plain",
        margin,
        tableWidth: printableW,
        styles: {
            font: "helvetica",
            fontSize: 11,
            cellPadding: 3,
            lineWidth: 0.2,
            valign: "middle",
        },
        headStyles: {
            fontStyle: "bold",
            fillColor: [3, 69, 144],
            textColor: [255, 255, 255],
        },
        footStyles: {
            fontStyle: "bold",
        },
        columnStyles: {
            0: {cellWidth: colW[0], halign: "left"},
            1: {cellWidth: colW[1], halign: "left"},
            2: {cellWidth: colW[2], halign: "left"}, // amounts line up perfectly
        },
        didParseCell: (data) => {
            if (data.section === "foot") {
                // Make "Total:" span first two columns and right-align it
                if (data.column.index === 0) {
                    data.cell.colSpan = 2;
                    data.cell.halign = "right";
                }

                // Clear the second column since it's merged
                if (data.column.index === 1) {
                    data.cell.text = "";
                }

                // Right-align the total amount
                if (data.column.index === 2) {
                    data.cell.halign = "left";
                    data.cell.cellPadding = { right: 6, left: 6, top: 3, bottom: 3 };
                }
            }
        },
    });
}

const generateFinancialStatement = async ({
                                              sfsIncome,
                                              sfsExpenditureFlexible,
                                              sfsAdditionalDebt,
                                              sfsExpenditureFixed,
                                              sfsFixedOther,
                                              sfsSavings,
                                              clientLogo,
                                              sfsTotals,
                                              disposableIncome,
                                          }) => {
    const doc = new jsPDF();

    // header
    try {
        const logo = await loadImage(clientLogo);
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, 250, 25, "F");
        doc.addImage(logo, "PNG", 10, 5, 65, 14);
    } catch (error) {
        console.error("Error loading logo:", error);
        doc.text("Company logo", 10, 10);
    }

    // disclaimer + customer details
    const info =
        "This document is provided for reference and informational purposes only. It is not intended to serve as an official regulatory document. Please do not use this document for any formal financial or legal purposes.";
    const wrappedInfo = doc.splitTextToSize(info.trim(), 270);

    const customerDetails = [
        [
            "My Disposable Income (monthly)",
            disposableIncome > 0
                ? `+ £${disposableIncome.toFixed(2)}`
                : `- £${Math.abs(disposableIncome).toFixed(2)}`,
        ],
        ["Date", new Date().toLocaleString().split(",")[0]],
    ];

    doc.setFontSize(11);
    doc.text(wrappedInfo, 10, 40);
    doc.autoTable({
        body: customerDetails,
        startY: 60,
        theme: "striped",
        margin: {top: 20, left: 10, right: 10},
        willDrawCell: (data) => {
            if (data.row.section === "head" || data.cell.text === "") {
                data.cell.styles.fillColor = null;
                data.cell.text = "";
                data.cell.styles.lineWidth = 0;
            }
        },
    });

    // shared layout values
    const margin = {left: 10, right: 10};
    const pageW = doc.internal.pageSize.getWidth();
    const printableW = pageW - margin.left - margin.right;

    // helper to compute section totals (uses your sfsTotals when present)
    const getSectionTotal = (type, processedData) => {
        if (type === "fixed") {
            const fixedNames = new Set(["Fixed Expenditure", "Fixed Other"]);
            const totalItems = (sfsTotals?.totals || []).filter((t) =>
                fixedNames.has(t.name)
            );
            if (totalItems.length > 0) {
                const combined = totalItems.find((t) => t.name === "Fixed Expenditure");
                return combined
                    ? combined.value
                    : totalItems.reduce((sum, t) => sum + (t.value || 0), 0);
            }
            // fallback: sum parsed amounts from the table rows
            return processedData.reduce((sum, row) => {
                const val = Number.parseFloat(String(row[2]).replaceAll(/[^0-9.-]/g, "")) || 0;
                return sum + val;
            }, 0);
        }

        if (type === "savings") return sfsSavings || 0;

        const totalItem = sfsTotals?.totals?.find(
            (item) => item.name === statementHeaders[type]
        );

        return totalItem ? totalItem.value : 0;
    };

    const statementTypes = ["income", "fixed", "flexible", "savings", "debts"];

    for (const type of statementTypes) {
        let rows = [];
        switch (type) {
            case "income":
                rows = processData(sfsIncome);
                break;
            case "fixed":
                rows = [
                    ...processData(sfsExpenditureFixed),
                    [
                        "Other essential costs",
                        frequencies.M,
                        sfsFixedOther ? `£${sfsFixedOther.toFixed(2)}` : "£0.00",
                    ],
                ];
                break;
            case "flexible":
                rows = processData(sfsExpenditureFlexible);
                break;
            case "savings":
                rows = [["Savings", frequencies.M, `£${sfsSavings.toFixed(2)}`]];
                break;
            case "debts":
                rows = processData(sfsAdditionalDebt);
                break;
        }

        if (!rows || rows.length === 0) continue;

        const total = getSectionTotal(type, rows);
        renderSection(
            doc,
            printableW,
            margin,
            statementHeaders[type],
            rows,
            total
        );
    }

    // footer page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(
            `Page ${i} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 20,
            doc.internal.pageSize.getHeight() - 10
        );
    }

    doc.save("financial-statement.pdf");
};

export default generateFinancialStatement;
